import { Form } from "react-bootstrap";
import { getFromDataPath } from "../../utility";
import { useArsContext } from "../contexts";
import { isAnalysisSet } from "../typeGuards";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";
import { WhereClauseManager } from "./WhereClauseManager";

/**
 * Props for the AnalysisSetControl component.
 */
export interface AnalysisSetControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisSet in ArsContext state */
  dataPath: string;
  /** Can rename the object */
  canRename?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * A component for managing AnalysisSet entities with full CRUD capabilities.
 *
 * This component provides:
 * - NamedObjectControl for editing name, description, and label
 * - WhereClauseManager for managing where clause conditions
 * - RemoveArsItemButton for deleting the analysis set
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisSet in ArsContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the analysis set editor.
 */
export const AnalysisSetControl = ({
  dataPath,
  canRename = false,
  disabled = false,
}: AnalysisSetControlProps): React.ReactElement => {
  const { state } = useArsContext();
  const analysisSet = getFromDataPath(dataPath, state, isAnalysisSet);
  // Disable editing for parent templates
  if (analysisSet?.disableEditing) disabled = true;
  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute bottom-0 end-0 m-1">
        <Form.Text>
          <small>Id: {dataPath.split("/").pop()}</small>
        </Form.Text>
      </div>
      {canRename && (
        <div className="position-absolute top-0 end-0 m-2">
          <RemoveArsItemButton
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      )}

      <NamedObjectControl
        dataPath={dataPath}
        canRename={canRename}
        disabled={disabled}
        labelSubText="This replaces [[Analysis set]] in the table. Enter a label to display the selected analysis set in the title."
      />

      <WhereClauseManager
        dataPath={dataPath}
        disabled={disabled}
      />
    </div>
  );
};

AnalysisSetControl.displayName = "AnalysisSetControl";
